import { StepperSelectionEvent } from '@angular/cdk/stepper';
import {
	AfterViewInit,
	Component,
	ElementRef,
	Inject,
	OnInit,
	Renderer2,
	ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import {
	AppModalService,
	ModalData,
} from '@datachain/ui-sdk/components/app-modal';
import { PanelAction } from '@datachain/ui-sdk/components/app-panel';
import { AppIcons } from '@datachain/ui-sdk/components/dc-icon';
import { Store } from '@ngrx/store';
import { Map } from 'immutable';
import {
	BehaviorSubject,
	combineLatest,
	firstValueFrom,
	map,
	Observable,
	ReplaySubject,
	takeUntil,
	tap,
} from 'rxjs';

import { ActionIcons } from '../../../../ui/app-actions.icons';
import { DcIcons } from '../../../../ui/app-dc.icons';
import { ActionIconsInjectable } from '../../../../ui/ui.module';
import { ColumnConfigItemEntity } from '../../../domain/column-config-item.entity';
import {
	SubscriptionDiagnosticError,
	SubscriptionErrorKey,
} from '../../../domain/diagnostic-errors.entity';
import { SubscriptionStatus } from '../../../domain/subscription.types';
import { SubscriptionProvider } from '../../../providers/subscription.provider';
import {
	refreshSubscriptionColumns,
	SubscriptionSelector,
} from '../../../store';
import { ColumnsConfigComponent } from '../../columns-config/columns-config.component';
import {
	ISubscriptionConfirmationModalData,
	SubscriptionActionConfirmPopupComponent,
	SubscriptionActionEnum,
} from '../../subscription-action-confirm-popup/subscription-action-confirm-popup.component';
import { ColumnsConfigViewProvider } from './columns-config-view.provider';

enum ColumnConfigStep {
	ColumnsSelection = 0,
	ColumnsProperties = 1,
}

@Component({
	selector: 'app-columns-config-modal',
	templateUrl: './columns-config-modal.component.html',
	styleUrls: ['./columns-config-modal.component.scss'],
	providers: [SubscriptionProvider, ColumnsConfigViewProvider],
})
export class ColumnsConfigModalComponent<T>
	extends DcBaseComponent
	implements OnInit, AfterViewInit
{
	protected readonly DcIcons = DcIcons;
	protected readonly ColumnConfigStep = ColumnConfigStep;
	protected readonly SubscriptionErrorKey = SubscriptionErrorKey;

	public vo$: Observable<{
		availableColumns: Map<string, ColumnConfigItemEntity>;
		subscriptionColumns: Map<string, ColumnConfigItemEntity>;
		currentStep: ColumnConfigStep;
		columnsPanelActions: Array<PanelAction>;
		stepValidity: boolean;
		isColumnsReady: boolean;
		error: SubscriptionDiagnosticError | undefined;
	}>;

	@ViewChild('columnsSelection')
	private readonly columnsSelectionCmp: ColumnsConfigComponent | null = null;
	@ViewChild('columnsProperties')
	private readonly columnsPropertiesCmp: ColumnsConfigComponent | null = null;

	@ViewChild(MatStepper)
	private readonly stepper: MatStepper | null = null;

	private readonly columnActions$ = new BehaviorSubject<Array<PanelAction>>([]);

	private readonly stepValidity$ = new BehaviorSubject<boolean>(false);

	private readonly currentStep$ = new BehaviorSubject<ColumnConfigStep>(
		ColumnConfigStep.ColumnsSelection
	);

	private readonly previousStep$ = new ReplaySubject<ColumnConfigStep>(1);

	private readonly datablockColumns$ = new BehaviorSubject<
		Map<string, ColumnConfigItemEntity>
	>(Map());

	private readonly isColumnsReady$ = new BehaviorSubject<boolean>(false);

	public constructor(
		private readonly subscriptionProvider: SubscriptionProvider,
		private readonly columnsConfigViewProvider: ColumnsConfigViewProvider,
		private readonly selector: SubscriptionSelector,
		private readonly store: Store,
		private readonly modalService: AppModalService,
		private readonly renderer: Renderer2,
		private readonly elemRef: ElementRef,
		@Inject(MAT_DIALOG_DATA) public readonly modalData: ModalData & T,
		@Inject(ActionIconsInjectable) private readonly actionIcons: AppIcons
	) {
		super();
		this.cmpId = 'columns-config-modal';

		this.subscriptionProvider.configure({
			[ColumnConfigStep.ColumnsSelection]: [
				SubscriptionErrorKey.NoSelectColumns,
			],
			[ColumnConfigStep.ColumnsProperties]: [
				SubscriptionErrorKey.InvalidSubscriptionColumns,
			],
		});

		const errors$ = combineLatest([
			this.subscriptionProvider.errors$,
			this.currentStep$,
		]).pipe(
			map(([, currentStep]) =>
				this.subscriptionProvider.getErrorsForStep(currentStep)
			),
			tap((currentErrors) => {
				this.stepValidity$.next(currentErrors.size === 0);
				return this.bootstrapIcons(currentErrors);
			})
		);

		this.vo$ = combineAll({
			availableColumns: this.datablockColumns$,
			subscriptionColumns: this.datablockColumns$.pipe(
				map((columns) => columns.filter((item) => item.isSelectedInSource))
			),
			currentStep: this.currentStep$,
			columnsPanelActions: this.columnActions$,
			stepValidity: this.stepValidity$,
			isColumnsReady: this.isColumnsReady$,
			error: errors$.pipe(map((errors) => errors.valueSeq().first())),
		});
	}

	public ngOnInit(): void {
		super.ngOnInit();
		this.handleSubscription();
	}

	public override ngAfterViewInit(): void {
		super.ngAfterViewInit();
		setTimeout(() => {
			this.columnsConfigViewProvider.adjustHeights(this.elemRef, this.renderer);
			this.isColumnsReady$.next(true);
		}, 750);
	}

	public backStep(): void {
		if (this.stepper === null) {
			return;
		}

		this.stepper.previous();
	}

	public nextStep(): void {
		if (this.stepper === null) {
			return;
		}

		this.stepper.next();
	}

	public refreshColumns(): void {
		this.store.dispatch(refreshSubscriptionColumns());
	}

	public changeCurrentStep(event: StepperSelectionEvent): void {
		this.stepValidity$.next(false);
		this.currentStep$.next(event.selectedIndex);
		this.previousStep$.next(event.previouslySelectedIndex);
	}

	public async onStepReady(): Promise<void> {
		const previousStep = await firstValueFrom(this.previousStep$);

		if (previousStep === ColumnConfigStep.ColumnsSelection) {
			if (!this.columnsSelectionCmp) {
				return;
			}

			const elements = await this.columnsSelectionCmp.getUpdatedColumns();

			this.datablockColumns$.next(elements);
		}

		if (previousStep === ColumnConfigStep.ColumnsProperties) {
			if (!this.columnsPropertiesCmp || !this.columnsSelectionCmp) {
				return;
			}

			let datablockColumns = await firstValueFrom(this.datablockColumns$);

			const columnsConfig = await this.columnsPropertiesCmp.getUpdatedColumns();

			columnsConfig.forEach((col) => {
				datablockColumns = datablockColumns.set(col.id, col);
			});

			this.datablockColumns$.next(datablockColumns);
		}
	}

	public async updateSubscriptionColumns(): Promise<void> {
		if (!this.columnsPropertiesCmp) {
			return;
		}

		const columns = await this.columnsPropertiesCmp.getUpdatedColumns();

		const publishedSubscription = await firstValueFrom(
			this.selector.getPublishedSubscription$()
		);

		if (publishedSubscription.status === SubscriptionStatus.Unpublish) {
			return this.modalService.closeActiveModal({
				columns,
			});
		}

		const consumers = await firstValueFrom(
			this.selector.getConsumedProjects$()
		);

		if (consumers.size === 0) {
			return this.modalService.closeActiveModal({
				columns,
			});
		}

		const subscriptionId = await firstValueFrom(
			this.selector.getSubscriptionId$()
		);

		const publicMetadata = await firstValueFrom(
			this.selector.getPublicMetadata$()
		);

		const modalConfirm =
			this.modalService.openPopup<ISubscriptionConfirmationModalData>(
				SubscriptionActionConfirmPopupComponent,
				{
					title: $localize`:i18n=@@subscription.action.confirmation.popup.updateColumns.header:`,
					subTitle: '',
					subscriptionId,
					element: publicMetadata.title,
					action: SubscriptionActionEnum.UpdateColumns,
				},
				{
					disableClose: true,
				}
			);

		const modalConfirmResult = await firstValueFrom(modalConfirm.afterClosed());

		if (!modalConfirmResult) {
			return;
		}

		this.modalService.closeActiveModal({
			columns,
		});
	}

	private handleSubscription(): void {
		this.selector
			.getSubscriptionColumns$()
			.pipe(
				tap((columns) => {
					this.datablockColumns$.next(columns);
				})
			)
			.subscribe();

		this.currentStep$
			.pipe(
				takeUntil(this.onDestroy$),
				tap(() => {
					setTimeout(
						() =>
							this.columnsConfigViewProvider.adjustHeights(
								this.elemRef,
								this.renderer
							),
						750
					);
				})
			)
			.subscribe();
	}

	private async bootstrapIcons(
		errors: Map<SubscriptionErrorKey, SubscriptionDiagnosticError>
	): Promise<void> {
		const actions = [ActionIcons.FilterWarning];
		const actionMap = await firstValueFrom(
			this.actionIcons.getFromRegistry(...actions)
		);
		if (errors.size === 0) {
			this.columnActions$.next([]);
			if (this.columnsPropertiesCmp) {
				this.columnsPropertiesCmp.clearFilter();
			}
			return;
		}

		if (errors.has(SubscriptionErrorKey.NoSelectColumns)) {
			this.columnActions$.next([]);
			return;
		}

		this.columnActions$.next([
			{
				order: 0,
				command: (): void => {
					if (this.columnsPropertiesCmp) {
						this.columnsPropertiesCmp.filterColumnConfigErrors();
					}
				},
				iconName: actionMap.get(ActionIcons.FilterWarning)?.iconName,
				tooltip: $localize`:@@subscription.columns.config.action.filterErrors:`,
				description: $localize`:@@subscription.columns.config.action.filterErrors:`,
				typed: '',
			},
		]);
	}
}
